import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const CostTracker = ({ totalCost, breakdown, travelers }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [budget, setBudget] = useState(50000);
  const [isEditingBudget, setIsEditingBudget] = useState(false);
  const [budgetInput, setBudgetInput] = useState(budget?.toString());

  const costPerPerson = totalCost / travelers;
  const budgetUsed = (totalCost / budget) * 100;
  const remaining = budget - totalCost;

  const handleBudgetSave = () => {
    const newBudget = parseFloat(budgetInput) || 0;
    if (newBudget > 0) {
      setBudget(newBudget);
    }
    setIsEditingBudget(false);
  };

  const categories = [
    { key: 'activities', label: 'Activities', icon: 'Waves', color: 'text-teal-600' },
    { key: 'accommodation', label: 'Accommodation', icon: 'Hotel', color: 'text-purple-600' },
    { key: 'food', label: 'Food & Dining', icon: 'Utensils', color: 'text-orange-600' },
    { key: 'transport', label: 'Transportation', icon: 'Car', color: 'text-blue-600' }
  ];

  const getBudgetStatus = () => {
    if (budgetUsed >= 100) return { color: 'text-red-600', icon: 'AlertTriangle', message: 'Over budget!' };
    if (budgetUsed >= 80) return { color: 'text-yellow-600', icon: 'AlertCircle', message: 'Approaching limit' };
    return { color: 'text-green-600', icon: 'CheckCircle', message: 'Within budget' };
  };

  const status = getBudgetStatus();

  if (!isExpanded) {
    return (
      <div className="w-16 bg-card border-l border-border flex flex-col items-center py-4 gap-4">
        <button
          onClick={() => setIsExpanded(true)}
          className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center hover:bg-accent/30 transition-colors"
          aria-label="Expand cost tracker"
        >
          <Icon name="ChevronLeft" size={20} className="text-accent" />
        </button>
        <div className="flex flex-col items-center gap-2">
          <Icon name="Wallet" size={18} className="text-accent" />
          <div className="text-xs font-bold text-foreground writing-mode-vertical transform rotate-180">
            ₱{(totalCost / 1000)?.toFixed(0)}k
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-80 bg-card border-l border-border p-6">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon name="Wallet" size={20} className="text-accent" />
            <h2 className="text-lg font-bold text-foreground">Cost Tracker</h2>
          </div>
          <button
            onClick={() => setIsExpanded(false)}
            className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
            aria-label="Collapse cost tracker"
          >
            <Icon name="ChevronRight" size={18} />
          </button>
        </div>
      </div>
      {/* Total Cost */}
      <div className="bg-gradient-to-br from-accent/20 to-accent/10 p-6 border-b border-border">
        <div className="mb-4">
          <p className="text-sm text-muted-foreground mb-1">Total Trip Cost</p>
          <p className="text-3xl font-bold text-foreground">₱{totalCost?.toLocaleString()}</p>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Per person ({travelers} travelers)</span>
          <span className="font-semibold text-foreground">₱{costPerPerson?.toLocaleString()}</span>
        </div>
      </div>
      {/* Budget Progress */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-foreground">Budget</span>
          {isEditingBudget ? (
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={budgetInput}
                onChange={(e) => setBudgetInput(e?.target?.value)}
                className="w-24 h-7 px-2 text-xs rounded border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                autoFocus
              />
              <button
                onClick={handleBudgetSave}
                className="text-xs text-primary hover:text-primary/80"
              >
                Save
              </button>
            </div>
          ) : (
            <button
              onClick={() => {
                setIsEditingBudget(true);
                setBudgetInput(budget?.toString());
              }}
              className="text-sm font-semibold text-primary hover:text-primary/80 flex items-center gap-1"
            >
              ₱{budget?.toLocaleString()}
              <Icon name="Edit" size={12} />
            </button>
          )}
        </div>
        
        <div className="w-full bg-muted rounded-full h-3 overflow-hidden mb-2">
          <div
            className={`h-full transition-all duration-500 ${
              budgetUsed >= 100 ? 'bg-red-500' : budgetUsed >= 80 ? 'bg-yellow-500' : 'bg-green-500'
            }`}
            style={{ width: `${Math.min(budgetUsed, 100)}%` }}
          />
        </div>
        
        <div className="flex items-center justify-between text-xs">
          <div className={`flex items-center gap-1 ${status?.color}`}>
            <Icon name={status?.icon} size={12} />
            <span className="font-medium">{status?.message}</span>
          </div>
          <span className="text-muted-foreground">{budgetUsed?.toFixed(1)}% used</span>
        </div>
        
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Remaining</span>
            <span className={`font-semibold ${
              remaining < 0 ? 'text-red-600' : 'text-green-600'
            }`}>
              ₱{Math.abs(remaining)?.toLocaleString()}
              {remaining < 0 && ' over'}
            </span>
          </div>
        </div>
      </div>
      {/* Category Breakdown */}
      <div className="flex-1 overflow-y-auto p-4">
        <h3 className="text-sm font-semibold text-foreground mb-4">Breakdown by Category</h3>
        <div className="space-y-4">
          {categories?.map((category) => {
            const amount = breakdown?.[category?.key] || 0;
            const percentage = totalCost > 0 ? (amount / totalCost) * 100 : 0;
            
            return (
              <div key={category?.key}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg ${category?.color} bg-opacity-20 flex items-center justify-center`}>
                      <Icon name={category?.icon} size={14} className={category?.color?.replace('bg-', 'text-')} />
                    </div>
                    <span className="text-sm text-foreground">{category?.label}</span>
                  </div>
                  <span className="text-sm font-semibold text-foreground">
                    ₱{amount?.toLocaleString()}
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-full ${category?.color} transition-all duration-500`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground text-right mt-1">
                  {percentage?.toFixed(1)}% of total
                </p>
              </div>
            );
          })}
        </div>
      </div>
      {/* Budget Tips */}
      <div className="p-4 border-t border-border bg-primary/5">
        <div className="flex items-start gap-2">
          <Icon name="Lightbulb" size={14} className="text-primary mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-xs font-semibold text-foreground mb-1">Budget Tip</h4>
            <p className="text-xs text-muted-foreground">
              {budgetUsed >= 80
                ? 'Consider removing or replacing some activities to stay within budget.' :'Set aside 10-15% extra for unexpected expenses and souvenirs.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostTracker;